/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Button, TextareaAutosize, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import { changeAdditionalNotes } from '../store/ContractAudit.actions';
import { ListType } from '../types';
import { useQueryClient } from 'react-query';

type AdditionalNotesProps = {
	notes: string;
	contractId: string;
};

const AdditionalNotes: FC<AdditionalNotesProps> = ({ notes, contractId }: AdditionalNotesProps) => {
	const [additionalNotes, setAdditionalNotes] = useState<string>(notes);
	const [newAdditionalNotes, setNewAdditionalNotes] = useState<string>(notes);
	const queryClient = useQueryClient();
	const handleChangeTextArea = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
		setNewAdditionalNotes(event.target.value);
	};

	const saveAdditionalNotes = (): void => {
		changeAdditionalNotes({
			changedNotes: newAdditionalNotes,
			contractId
		}).then(() => queryClient.resetQueries([ListType.CONTRACT, { tenantIds: [], appVariantIds: [], search: '' }]));

		setAdditionalNotes(newAdditionalNotes);
	};

	return (
		<>
			<TextareaAutosize
				aria-label="additionalNotes"
				style={{ width: '50%' }}
				value={newAdditionalNotes}
				onChange={handleChangeTextArea}
			/>

			<Typography>
				<Button
					variant="outlined"
					startIcon={<CheckIcon />}
					size="small"
					style={{ marginRight: '0.25rem' }}
					disabled={newAdditionalNotes === additionalNotes}
					onClick={saveAdditionalNotes}
				>
					Speichern
				</Button>
				<Button
					variant="contained"
					startIcon={<CancelIcon />}
					size="small"
					disabled={newAdditionalNotes === additionalNotes}
					onClick={(): void => {
						setNewAdditionalNotes(additionalNotes);
					}}
				>
					Abbrechen
				</Button>
			</Typography>
		</>
	);
};

export default AdditionalNotes;
